import { httpClient, toClientApiError } from "./httpClient";

export type Subscription = {
  id: string;
  customerId: string;
  projectId: string | null;
  categoryId: string | null;
  contactId: string | null;
  title: string;
  status: string;
  amountAgorot: number;
  currency: string;
  paymentArrangement: string | null;
  lastChargeStatusKey?: string | null;
  lastChargeOn?: string | null;
  nedarimKevaId?: string | null;
  nedarimChannel?: string | null;
  startsOn: string | null;
  endsOn: string | null;
  notes: string | null;
  attributes?: Array<{
    fieldKey: string;
    labelHe: string;
    valueType: "string" | "number" | "boolean" | "date" | "enum";
    value: string | number | boolean;
  }>;
  createdAt: string;
  updatedAt: string;
};

export type Payment = {
  id: string;
  customerId: string;
  subscriptionId: string | null;
  projectId: string | null;
  amountAgorot: number;
  currency: string;
  status: string;
  method: string;
  paidAt: string;
  reference: string | null;
  notes: string | null;
  createdAt: string;
};

export type Expense = {
  id: string;
  customerId: string | null;
  projectId: string | null;
  title: string;
  amountAgorot: number;
  currency: string;
  status: string;
  incurredAt: string;
  notes: string | null;
  createdAt: string;
};

export type Balance = {
  id: string | null;
  customerId: string;
  amountAgorot: number;
  currency: string;
  updatedAt: string | null;
};

export type LedgerMovementKind = "charge" | "payment" | "expense_allocation";

export type LedgerMovement = {
  id: string;
  customerId: string;
  projectId: string | null;
  kind: LedgerMovementKind;
  amountAgorot: number;
  currency: string;
  subscriptionId: string | null;
  paymentId: string | null;
  expenseId: string | null;
  occurredAt: string;
  note: string | null;
  createdAt: string;
};

async function wrap<T>(fn: () => Promise<T>): Promise<T> {
  try {
    return await fn();
  } catch (error) {
    throw toClientApiError(error);
  }
}

function formatShekels(agorot: number): string {
  return (agorot / 100).toLocaleString("he-IL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export { formatShekels };

export const financeApi = {
  listSubscriptions(customerId?: string): Promise<Subscription[]> {
    return wrap(async () => {
      const suffix = customerId ? `?customerId=${encodeURIComponent(customerId)}` : "";
      const res = await httpClient.get<{ items: Subscription[] }>(
        `/subscriptions${suffix}`,
      );
      return res.data.items;
    });
  },

  createSubscription(input: {
    customerId: string;
    title: string;
    amountAgorot: number;
    notes?: string;
    categoryId?: string | null;
    contactId?: string | null;
    paymentArrangement?: string | null;
    status?: string;
  }): Promise<Subscription> {
    return wrap(async () => {
      const res = await httpClient.post<{ item: Subscription }>("/subscriptions", input);
      return res.data.item;
    });
  },

  updateSubscription(
    id: string,
    input: {
      title?: string;
      status?: string;
      notes?: string | null;
      categoryId?: string | null;
      contactId?: string | null;
      paymentArrangement?: string | null;
      amountAgorot?: number;
    },
  ): Promise<Subscription> {
    return wrap(async () => {
      const res = await httpClient.patch<{ item: Subscription }>(
        `/subscriptions/${encodeURIComponent(id)}`,
        input,
      );
      return res.data.item;
    });
  },

  listPayments(customerId: string): Promise<Payment[]> {
    return wrap(async () => {
      const res = await httpClient.get<{ items: Payment[] }>(
        `/payments?customerId=${encodeURIComponent(customerId)}`,
      );
      return res.data.items;
    });
  },

  createPayment(input: {
    customerId: string;
    amountAgorot: number;
    method?: "manual" | "bank_transfer";
    reference?: string;
    subscriptionId?: string;
  }): Promise<Payment> {
    return wrap(async () => {
      const res = await httpClient.post<{ item: Payment }>("/payments", input);
      return res.data.item;
    });
  },

  listExpenses(customerId: string): Promise<Expense[]> {
    return wrap(async () => {
      const res = await httpClient.get<{ items: Expense[] }>(
        `/expenses?customerId=${encodeURIComponent(customerId)}`,
      );
      return res.data.items;
    });
  },

  createExpense(input: {
    customerId: string;
    title: string;
    amountAgorot: number;
    notes?: string;
  }): Promise<Expense> {
    return wrap(async () => {
      const res = await httpClient.post<{ item: Expense }>("/expenses", input);
      return res.data.item;
    });
  },

  getBalance(customerId: string): Promise<Balance> {
    return wrap(async () => {
      const res = await httpClient.get<{ item: Balance }>(
        `/balances?customerId=${encodeURIComponent(customerId)}`,
      );
      return res.data.item;
    });
  },

  listMovements(customerId: string): Promise<LedgerMovement[]> {
    return wrap(async () => {
      const res = await httpClient.get<{ items: LedgerMovement[] }>(
        `/ledger-movements?customerId=${encodeURIComponent(customerId)}`,
      );
      return res.data.items;
    });
  },
};
